import { createFileRoute } from "@tanstack/react-router";
import { byCategory } from "@/data/products";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/other-creations")({
  head: () => ({
    meta: [
      { title: "Cornhole Boards & Other Custom Creations | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Custom cornhole boards, fidget spinners and one-off personalized creations built to order in Paragould, Arkansas.",
      },
      { property: "og:title", content: "Cornhole boards and other custom creations" },
      {
        property: "og:description",
        content: "The projects that do not fit a category — built and personalized to order by Paul.",
      },
      { property: "og:url", content: pageUrl("/other-creations") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/other-creations") }],
  }),
  component: OtherCreations,
});

function OtherCreations() {
  const items = byCategory("other");
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Other Creations" }]} />
      <PageHeader
        eyebrow="Other creations"
        title="The projects that do not fit a category."
        lede="Cornhole boards, fidget spinners and the one-off requests that arrive because someone had an idea and nowhere else to take it."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="other-cornhole-heading" id="cornhole">
        <div className="shell grid gap-12 lg:grid-cols-2 lg:items-start">
          <SectionHeading
            id="other-cornhole-heading"
            eyebrow="Cornhole boards"
            title="Built flat, finished smooth, painted yours."
            lede="Boards are built and sanded for a true playing surface, then personalized with painted or carved artwork — team colors, family names or a business mark you own."
          />
          <div className="surface-card p-8">
            <h3 className="text-base font-semibold">Before you order a set</h3>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>Confirm the artwork you want and whether you have the right to use it.</li>
              <li>Decide on painted artwork, carved artwork or a combination.</li>
              <li>Ask Paul about current build options, timing and delivery before you commit.</li>
            </ul>
            <div className="mt-6">
              <CtaLink to="/design-your-piece" variant="outline" arrow className="h-11">
                Ask about a set
              </CtaLink>
            </div>
          </div>
        </div>
      </Section>

      <Section labelledBy="other-items-heading">
        <div className="shell">
          <SectionHeading
            id="other-items-heading"
            eyebrow="Recent pieces"
            title="A few examples."
            lede="This category is deliberately open. If your idea does not appear anywhere else on this site, it belongs here."
          />
          <ProductGrid products={items} />
        </div>
      </Section>

      <FinalCTA heading="If you cannot find it anywhere on this site, describe it." />
    </>
  );
}
